/**
 * Team & access API (/access, /org/departments).
 *
 * People have ONE login and many assignments. An assignment = a role + where
 * it applies (whole company · a business · one branch) + optional department
 * and job title. Departments are organisation only — they never grant access.
 */
import api from "@/services/api";

export type Level = "cashier" | "staff" | "business_admin" | "super_admin" | "tenant_owner";

export interface Assignment {
  _id: string;
  businessId: string | null;
  businessName: string;
  branchId: string | null;
  branchName: string | null;
  roleId: string;
  roleName: string;
  roleSlug: string | null;
  level: Level;
  departmentId: string | null;
  departmentName: string | null;
  title: string | null;
  employeeId: string | null;
  active: boolean;
  assignedAt: string;
  expiresAt: string | null;
  revokedAt?: string | null;
  revokeReason?: string | null;
}

export interface Person {
  _id: string;
  name: string;
  email: string;
  phone: string | null;
  status: "pending" | "active" | "suspended" | "inactive";
  accountRole: Level;
  isOwner: boolean;
  isSelf: boolean;
  lastLogin: string | null;
  createdAt: string;
  employee: { _id: string; name: string; employeeNumber: string; businessId: string } | null;
  /** 2-step sign-in is on (for "Reset 2-step"). */
  twoStep?: boolean;
  assignments: Assignment[];
  history?: Assignment[];
  canEdit?: boolean;
}

export interface CapabilityGroup {
  key: string;
  label: string;
  hint: string | null;
  always: boolean;
  level: "full" | "some" | "none";
  allowed: Array<{ key: string; description: string }>;
  notAllowed: number;
  notInPlan: boolean;
}

export interface AccessSummary {
  groups: CapabilityGroup[];
  summary: { full: string[]; some: string[]; none: string[]; seesCosts: boolean; canManageAccess: boolean };
}

export interface RoleOption {
  _id: string;
  name: string;
  slug: string;
  description: string;
  level: Level;
  isSystemRole: boolean;
  canGive: boolean;
  whyNot: string | null;
  access: AccessSummary;
}

export interface RolesForPlace {
  place: { businessId: string | null; businessName: string; branchId: string | null; industry: string | null };
  roles: RoleOption[];
}

export interface Structure {
  canCompanyWide: boolean;
  isOwner: boolean;
  businesses: Array<{ _id: string; name: string; industry: string | null; wholeBusiness: boolean; branches: Array<{ _id: string; name: string; isDefault: boolean; kind: string }> }>;
}

export interface Department {
  _id: string;
  name: string;
  code: string;
  description: string;
  scope: "company" | "business" | "branch";
  businessId: string | null;
  businessName: string | null;
  branchId: string | null;
  branchName: string | null;
  parentId: string | null;
  headEmployeeId: string | null;
  headName: string | null;
  isActive: boolean;
  archivedAt: string | null;
  people: number;
  staffRecords: number;
}

export interface MyPlace {
  businessId: string;
  name: string;
  wholeBusiness: boolean;
  homeBranchId: string | null;
  branches: Array<{ _id: string; name: string; isDefault: boolean; roles: string[] }>;
}

export interface AssignmentInput {
  businessId: string | null;
  branchId?: string | null;
  roleId: string;
  departmentId?: string | null;
  title?: string | null;
  employeeId?: string | null;
  expiresAt?: string | null;
}

const data = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);
const clean = (o: Record<string, unknown>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ""));

export const accessKeys = {
  all: ["access"] as const,
  structure: () => ["access", "structure"] as const,
  people: (q: object) => ["access", "people", q] as const,
  person: (id: string) => ["access", "person", id] as const,
  roles: (businessId: string | null, branchId: string | null) => ["access", "roles", businessId, branchId] as const,
  rolesEditor: () => ["access", "roles-editor"] as const,
  effective: (id: string, businessId: string | null, branchId: string | null) => ["access", "effective", id, businessId, branchId] as const,
  departments: (q: object) => ["access", "departments", q] as const,
  myPlaces: () => ["access", "my-places"] as const,
};

// ─── Custom roles (11a) ───────────────────────────────────────────
export type CustomLevel = "cashier" | "staff" | "business_admin";
export interface EditorPermission { key: string; description: string; canGive: boolean; addon: string | null }
export interface EditorGroup { key: string; label: string; hint: string | null; always: boolean; industries: string[] | null; permissions: EditorPermission[] }
export interface EditorRole { _id: string; name: string; slug: string; description: string; level: Level; isSystemRole: boolean; isActive: boolean; permissions: string[]; peopleCount: number }
/** 12d: ready-made starting points for an industry (only permissions you can give). */
export interface RolePreset { key: string; name: string; level: CustomLevel; industries: string[]; description: string; permissions: string[] }
export interface RolesEditor { canManage: boolean; levels: CustomLevel[]; maxRoles: number; groups: EditorGroup[]; roles: EditorRole[]; presets?: RolePreset[] }
export interface RoleInput { name?: string; description?: string; level?: CustomLevel; permissions?: string[]; copyFrom?: string; isActive?: boolean }

export const accessService = {
  rolesEditor: () => data<RolesEditor>(api.get("/access/roles/editor")),
  createRole: (body: RoleInput) => data<EditorRole>(api.post("/access/roles", body)),
  updateRole: (id: string, body: RoleInput) => data<EditorRole>(api.put(`/access/roles/${id}`, body)),
  retireRole: (id: string) => data<EditorRole>(api.delete(`/access/roles/${id}`)),
  structure: () => data<Structure>(api.get("/access/structure")),
  myPlaces: () => data<MyPlace[]>(api.get("/access/me/places")),
  people: (q: { businessId?: string; departmentId?: string; q?: string; status?: string } = {}) => data<Person[]>(api.get("/access/people", { params: clean(q) })),
  person: (id: string) => data<Person>(api.get(`/access/people/${id}`)),
  createPerson: (body: { name: string; email: string; phone?: string; password: string; employeeId?: string | null; assignment: AssignmentInput }) => data<Person>(api.post("/access/people", body)),
  invite: (body: { email: string; businessId: string | null; branchId?: string | null; roleId: string; departmentId?: string | null; employeeId?: string | null; title?: string | null; note?: string }) =>
    data<unknown>(api.post("/user-invitations", body)),
  updatePerson: (id: string, body: { name?: string; phone?: string; email?: string }) => data<Person>(api.put(`/access/people/${id}`, body)),
  setPassword: (id: string, password: string) => data<unknown>(api.post(`/access/people/${id}/password`, { password })),
  deactivate: (id: string, reason?: string) => data<unknown>(api.post(`/access/people/${id}/deactivate`, { reason })),
  reactivate: (id: string) => data<Person>(api.post(`/access/people/${id}/reactivate`)),
  effective: (id: string, businessId: string | null, branchId: string | null) =>
    data<{ place: { businessName: string }; access: AccessSummary }>(api.get(`/access/people/${id}/effective`, { params: clean({ businessId, branchId }) })),
  addAssignment: (userId: string, body: AssignmentInput) => data<unknown>(api.post(`/access/people/${userId}/assignments`, body)),
  updateAssignment: (id: string, body: Partial<AssignmentInput>) => data<unknown>(api.put(`/access/assignments/${id}`, body)),
  endAssignment: (id: string, reason?: string) => data<unknown>(api.delete(`/access/assignments/${id}`, { data: { reason } })),
  roles: (businessId: string | null, branchId: string | null) => data<RolesForPlace>(api.get("/access/roles", { params: clean({ businessId, branchId }) })),
  departments: (q: { businessId?: string; includeArchived?: boolean } = {}) => data<Department[]>(api.get("/org/departments", { params: clean(q as Record<string, unknown>) })),
  createDepartment: (body: Partial<Department>) => data<Department>(api.post("/org/departments", body)),
  updateDepartment: (id: string, body: Partial<Department>) => data<Department>(api.put(`/org/departments/${id}`, body)),
  archiveDepartment: (id: string, moveTo?: string | null) => data<Department>(api.post(`/org/departments/${id}/archive`, { moveTo: moveTo || null })),
};

export const LEVEL_LABEL: Record<Level, string> = {
  cashier: "Cashier level",
  staff: "Staff level",
  business_admin: "Manager level",
  super_admin: "Company admin",
  tenant_owner: "Owner",
};

export const INDUSTRY_LABEL: Record<string, string> = {
  retail: "General retail",
  restaurant: "Restaurant",
  salon: "Salon",
  pharmacy: "Pharmacy",
  hardware: "Hardware",
  wholesale: "Wholesale",
  services: "Services",
  gym: "Gym",
  construction: "Construction",
  other: "Other",
};

/** "Main Supermarket · Osu branch" / "Main Supermarket (all branches)" / "Whole company" */
export function placeLabel(a: Pick<Assignment, "businessId" | "businessName" | "branchName">): string {
  if (!a.businessId) return "Whole company";
  return a.branchName ? `${a.businessName} · ${a.branchName}` : `${a.businessName} (all branches)`;
}
